import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import { Container, FormControl, FormLabel, Grid, HStack, Select } from '@tq/ui-components';

import { useConsulteeTeams } from 'api';
import {
  ManageTeamFilterPermissionSelect,
  ManageTeamInviteUser,
  ManageTeamUsers,
  PageHeading,
  SearchText,
} from 'components';
import { useGetManageTeamConsulteeRecords } from './hooks';
import {
  manageUsersFilterPermissionState,
  manageUsersFilterTeamState,
  manageUsersSearchState,
} from 'store';
import { filterFormControlStyle, filterSelectStyle } from 'styles';
import { filterContainerStyle } from '../ManageConsultees/styles';
import { ConsulteeAdminProvider } from '../../components/ConsulteeAdminProvider';

export const ManageTeam: React.FC = () => {
  /**
   * Returns the main container UI for maintaining Consultees by Consultee Admins.
   *
   * @remarks
   *
   */
  const { t } = useTranslation('manage_team');
  const { data: teamOptions } = useConsulteeTeams();
  const { userData, totalCount } = useGetManageTeamConsulteeRecords();

  const [teamFilter, setTeamFilter] = useRecoilState(manageUsersFilterTeamState);

  return (
    <ConsulteeAdminProvider>
      <Container px={{ base: 0, lg: 4, xl: 0 }}>
        <Grid gridGap={2} mb='3rem'>
          <PageHeading heading={t('manage_team', 'Manage team')} />
          <p>
            {t(
              'manage_team_summary',
              'You can manage users in this space as well as invite team members.'
            )}
          </p>
          <ManageTeamInviteUser
            buttonText={t('button_text_send', 'Send Invite')}
            title={t('invite_user', 'Invite user')}
            teamOptions={teamOptions?.teams}
            pageType='ManageTeam'
          />
          <HStack gridGap={2} id='team-filter' sx={filterContainerStyle}>
            <FormControl sx={filterFormControlStyle}>
              <FormLabel display={{ base: 'none', lg: 'block' }}>
                {t('search_label', 'Search')}:
              </FormLabel>
              <SearchText
                searchState={manageUsersSearchState}
                placeholderText={t('search_placeholder', 'Search user')}
                recordCount={userData.length}
                totalCount={totalCount}
              />
            </FormControl>
            <FormControl sx={filterFormControlStyle}>
              <FormLabel htmlFor='team' mb={{ base: 0, lg: '.75rem' }}>
                {t('team_label', 'Team')}:
              </FormLabel>
              <Select
                inputId='team'
                onChange={(value) => setTeamFilter(Number(value))}
                defaultValue={teamFilter.toString()}
                sx={filterSelectStyle}
                aria-label='Team selection dropdown'
              >
                <option key='all' value={0}>
                  {t('all', 'All')}
                </option>
                {teamOptions?.teams.map((team) => (
                  <option key={team.value} value={team.value}>
                    {team.label}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={filterFormControlStyle}>
              <FormLabel htmlFor='permission-row' mb={{ base: 0, lg: '.75rem' }}>
                {t('permission_label', 'Permissions')}:
              </FormLabel>

              <ManageTeamFilterPermissionSelect
                permissionState={manageUsersFilterPermissionState}
                includeAllOption
                sx={filterSelectStyle}
                inputId='permission-row'
              />
            </FormControl>
          </HStack>
          <ManageTeamUsers applicationUsers={userData} />
        </Grid>
      </Container>
    </ConsulteeAdminProvider>
  );
};
